import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';

// Создаем контекст авторизации
const AuthContext = createContext(null);

// Провайдер контекста авторизации
export const AuthProvider = ({ children }) => {
  const [isTelegramAuthRequiredAndMissing, setTelegramAuthRequiredAndMissing] = useState(false);

  // Инициализируем начальное состояние пользователя из Telegram WebApp, если доступно
  const getInitialUserState = () => {
    const tgApp = window.Telegram?.WebApp;
    console.log('[AuthContext] Telegram WebApp доступен:', !!tgApp);

    if (tgApp && tgApp.initDataUnsafe && tgApp.initDataUnsafe.user) {
      const tgUser = tgApp.initDataUnsafe.user;
      console.log('[AuthContext] Получены данные пользователя из Telegram WebApp:', tgUser);
      // Важно: setTelegramAuthRequiredAndMissing(false) будет вызван здесь,
      // и это состояние будет использовано в useEffect.
      setTelegramAuthRequiredAndMissing(false); 
      return {
        telegramId: tgUser.id,
        firstName: tgUser.first_name,
        lastName: tgUser.last_name || '',
        username: tgUser.username || '',
      };
    } else {
      // Если данные Telegram отсутствуют, это запуск вне Telegram или с проблемой API
      console.log('[AuthContext] Telegram WebApp данные отсутствуют. Установка флага требования авторизации Telegram.');
      // Важно: setTelegramAuthRequiredAndMissing(true) будет вызван здесь.
      setTelegramAuthRequiredAndMissing(true);
      
      // В этом случае, даже если есть что-то в localStorage, мы все равно требуем Telegram.
      // Возвращаем пользователя-гостя, так как основная авторизация не прошла.
      // Флаг isTelegramAuthRequiredAndMissing уже установлен в true.
      const savedUser = localStorage.getItem('user');
      const savedTelegramId = localStorage.getItem('telegram_id');
      console.log('[AuthContext] Данные из localStorage (игнорируются для доступа, но могут быть для информации):', { savedUser, savedTelegramId });

      // Можно вернуть данные из localStorage, если они нужны для отображения чего-то,
      // но isTelegramAuthRequiredAndMissing все равно будет true.
      // Для простоты и строгости вернем гостя.
      return {
        telegramId: 0,
        firstName: 'Гость',
        lastName: '',
        username: 'guest'
      };
    }
  };

  const [user, setUser] = useState(getInitialUserState); // getInitialUserState вызовет setTelegramAuthRequiredAndMissing
  const [loading, setLoading] = useState(true);

  // При инициализации компонента авторизуем пользователя и проверяем блокировку
  useEffect(() => {
    // userFromState здесь будет содержать начальное состояние пользователя,
    // которое было установлено при инициализации useState(getInitialUserState).
    const userFromState = user; 

    // isTelegramAuthRequiredAndMissing также берется из состояния, которое было установлено getInitialUserState
    // Это важно, так как getInitialUserState вызывается до первого рендера и до этого useEffect.
    // Мы используем функциональное обновление для useState, чтобы получить самое свежее значение,
    // но в данном случае isTelegramAuthRequiredAndMissing уже должно быть корректно установлено.

    const checkAuth = async () => {
      // Если флаг isTelegramAuthRequiredAndMissing уже true (установлен в getInitialUserState),
      // то нет смысла пытаться авторизоваться через API, так как это не Telegram-контекст.
      if (isTelegramAuthRequiredAndMissing) {
        console.log('[AuthContext] Запуск вне Telegram или API недоступно, авторизация через API пропускается.');
        setLoading(false);
        return;
      }

      setLoading(true); 
      try {
        const requestBody = {
          telegramId: userFromState.telegramId,
          firstName: userFromState.firstName,
          lastName: userFromState.lastName,
          username: userFromState.username,
        };

        console.log('[AuthContext] Отправка запроса на авторизацию с данными:', requestBody);
        const response = await fetch('/api/users/auth', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Telegram-ID': String(requestBody.telegramId) // Убедимся, что это строка
          },
          body: JSON.stringify(requestBody)
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log('[AuthContext] Авторизация успешна, получен ответ:', result);
          
          if (result.user) {
            const serverUser = {
              ...result.user,
              isAdmin: result.user.isAdmin === true,
              isBlocked: result.user.isBlocked === true
            };
            
            // Сравниваем полученного пользователя с текущим состоянием userFromState
            // Обновляем состояние только если есть реальные изменения
            if (JSON.stringify(serverUser) !== JSON.stringify(userFromState)) {
              console.log('[AuthContext] Обновление пользователя данными с сервера:', serverUser);
              localStorage.setItem('user', JSON.stringify(serverUser));
              localStorage.setItem('telegram_id', String(serverUser.telegramId));
              setUser(serverUser);
            } else {
              console.log('[AuthContext] Данные с сервера идентичны текущим, setUser не вызывается.');
            }
            
            if (serverUser.isBlocked) {
              if (window.Telegram?.WebApp) {
                window.Telegram.WebApp.showAlert('Вы заблокированы в этом боте навсегда.');
              } else {
                alert('Вы заблокированы в этом боте навсегда.');
              }
            }
          } else {
            // Если сервер не вернул user, но ответил OK, это странно.
            // Не будем менять user, если он уже есть, или оставим userFromState.
            console.warn('[AuthContext] Сервер ответил OK, но не вернул пользователя. Используем начальное состояние.');
            // Если userFromState отличается от текущего user (маловероятно здесь, но для полноты)
            // и userFromState не пустой (telegramId !== 0)
            if (userFromState.telegramId !== 0 && JSON.stringify(user) !== JSON.stringify(userFromState)) {
                 localStorage.setItem('user', JSON.stringify(userFromState));
                 localStorage.setItem('telegram_id', String(userFromState.telegramId));
                 setUser(userFromState);
            }
          }
        } else {
          // Если ответ не OK, не меняем состояние пользователя, если оно уже установлено.
          // Это предотвратит сброс на "Гостя", если пользователь уже был загружен.
          console.error(`[AuthContext] Ошибка авторизации: ${response.status}. Текущий пользователь не изменен.`);
        }
      } catch (error) {
        console.error('[AuthContext] Исключение при авторизации:', error);
        // При ошибке сети и т.п. не меняем состояние пользователя, если оно уже установлено.
      } finally {
        setLoading(false);
      }
    };
    
    // Вызываем checkAuth только если telegramId в userFromState не равен 0 (т.е. это не дефолтный "Гость")
    // или если это первый запуск (когда user может быть дефолтным, но мы все равно хотим проверить localStorage/TG)
    // Однако, getInitialUserState уже обрабатывает localStorage/TG, так что userFromState уже должен быть корректным.
    // Простой вызов checkAuth() здесь должен быть безопасен, так как он выполняется один раз.
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, []); // Пустой массив зависимостей - эффект запускается только при монтировании компонента
  
  // Функция авторизации
  const login = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('telegram_id', userData.telegramId);
    setUser(userData);
  };
  
  // Функция выхода из системы
  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('telegram_id');
    setUser(null);
  };
  
  // Проверка, является ли пользователь администратором
  const isAdmin = user && user.isAdmin === true;
  
  // Проверка, заблокирован ли пользователь
  const isBlocked = user && user.isBlocked === true;
  
  // Значения, предоставляемые контекстом, обернутые в useMemo для стабильности
  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAdmin,
    isBlocked,
    isAuthenticated: !!user && !isBlocked && !isTelegramAuthRequiredAndMissing,
    isTelegramAuthRequiredAndMissing
  }), [user, loading, isAdmin, isBlocked, isTelegramAuthRequiredAndMissing]);
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Хук для использования контекста авторизации
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};
